
import { useState } from 'react';

type RequestType = 'party' | 'womens' | '';

interface FormData {
  name: string;
  email: string;
  phone: string;
  requestType: RequestType;
  // Party package fields
  eventDate: string;
  guestCount: string;
  childAge: string;
  partyTheme: string;
  partyBudget: string;
  // Women's collection fields
  collectionInterest: string;
  sizes: string;
  stylePreference: string;
  // Shared
  message: string;
}

const initialForm: FormData = {
  name: '',
  email: '',
  phone: '',
  requestType: '',
  eventDate: '',
  guestCount: '',
  childAge: '',
  partyTheme: '',
  partyBudget: '',
  collectionInterest: '',
  sizes: '',
  stylePreference: '',
  message: '',
};

export default function CallToAction() {
  const [form, setForm] = useState<FormData>(initialForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});

  const set = (field: keyof FormData, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const validate = () => {
    const e: Partial<Record<keyof FormData, string>> = {};
    if (!form.name.trim()) e.name = 'Name is required';
    if (!form.email || !form.email.includes('@')) e.email = 'Valid email is required';
    if (!form.requestType) e.requestType = 'Please select a request type';
    if (form.requestType === 'party') {
      if (!form.eventDate) e.eventDate = 'Event date is required';
      if (!form.guestCount) e.guestCount = 'Guest count is required';
    }
    if (form.message.length > 500) e.message = 'Message must be 500 characters or fewer';
    return e;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }

    setIsSubmitting(true);
    setStatus('idle');

    const payload: Record<string, string> = {
      name: form.name,
      email: form.email,
      phone: form.phone,
      requestType: form.requestType === 'party' ? 'Party Package' : "Women's Collection",
      message: form.message,
    };

    if (form.requestType === 'party') {
      payload.eventDate = form.eventDate;
      payload.guestCount = form.guestCount;
      payload.childAge = form.childAge;
      payload.partyTheme = form.partyTheme;
      payload.partyBudget = form.partyBudget;
    } else if (form.requestType === 'womens') {
      payload.collectionInterest = form.collectionInterest;
      payload.sizes = form.sizes;
      payload.stylePreference = form.stylePreference;
    }

    try {
      const res = await fetch('https://readdy.ai/api/form/d6gsl2b5pk3jcsh37dq0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(payload).toString(),
      });
      if (res.ok) { setStatus('success'); setForm(initialForm); }
      else setStatus('error');
    } catch {
      setStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputCls = (field: keyof FormData) =>
    `w-full px-4 py-3 rounded-lg bg-white/95 text-[#3a2a00] placeholder:text-[#936A0B]/50 text-sm focus:outline-none focus:ring-2 ${errors[field] ? 'ring-2 ring-red-400' : 'focus:ring-[#574407]'}`;

  const labelCls = 'block text-white/90 text-xs font-semibold uppercase tracking-wider mb-1.5';

  return (
    <section className="bg-[#EB9830] py-20 px-6">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <h2 className="text-4xl font-bold text-white mb-3">Make an Enquiry</h2>
          <p className="text-white/90 text-base">
            Tell us what you're looking for — we'll get back to you within 24 hours.
          </p>
        </div>

        {status === 'success' ? (
          <div className="bg-white/20 rounded-2xl p-12 text-center">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-white/30 rounded-full">
              <i className="ri-check-line text-3xl text-white"></i>
            </div>
            <h3 className="text-2xl font-bold text-white mb-2">Request Received!</h3>
            <p className="text-white/90 mb-6">Thank you! Our team will be in touch with you shortly.</p>
            <button
              onClick={() => setStatus('idle')}
              className="px-6 py-2.5 bg-[#574407] text-white rounded-full text-sm font-semibold hover:bg-[#3a2a00] transition-colors cursor-pointer whitespace-nowrap"
            >
              Submit Another Request
            </button>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            data-readdy-form
            id="abexia-customer-request"
            className="bg-white/15 backdrop-blur-sm rounded-2xl p-8 space-y-6"
          >
            {/* Request Type */}
            <div>
              <label className={labelCls}>I'm interested in *</label>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { val: 'party', icon: 'ri-cake-line', label: 'Party Packages' },
                  { val: 'womens', icon: 'ri-shopping-bag-line', label: "Women's Collections" },
                ].map(opt => (
                  <button
                    key={opt.val}
                    type="button"
                    onClick={() => set('requestType', opt.val)}
                    className={`flex items-center gap-3 px-4 py-3.5 rounded-xl border-2 transition-all cursor-pointer whitespace-nowrap text-sm font-semibold ${
                      form.requestType === opt.val
                        ? 'bg-[#574407] border-[#574407] text-white'
                        : 'bg-white/20 border-white/40 text-white hover:bg-white/30'
                    }`}
                  >
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className={`${opt.icon} text-lg`}></i>
                    </div>
                    {opt.label}
                  </button>
                ))}
              </div>
              {errors.requestType && <p className="text-red-200 text-xs mt-1">{errors.requestType}</p>}
            </div>

            {/* Contact Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Full Name *</label>
                <input
                  name="name"
                  type="text"
                  value={form.name}
                  onChange={e => set('name', e.target.value)}
                  placeholder="Your full name"
                  className={inputCls('name')}
                />
                {errors.name && <p className="text-red-200 text-xs mt-1">{errors.name}</p>}
              </div>
              <div>
                <label className={labelCls}>Email Address *</label>
                <input
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={e => set('email', e.target.value)}
                  placeholder="you@example.com"
                  className={inputCls('email')}
                />
                {errors.email && <p className="text-red-200 text-xs mt-1">{errors.email}</p>}
              </div>
              <div className="md:col-span-2">
                <label className={labelCls}>Phone Number</label>
                <input
                  name="phone"
                  type="tel"
                  value={form.phone}
                  onChange={e => set('phone', e.target.value)}
                  placeholder="+1 (555) 000-0000"
                  className={inputCls('phone')}
                />
              </div>
            </div>

            {/* Party Package Fields */}
            {form.requestType === 'party' && (
              <div className="space-y-4 border-t border-white/20 pt-5">
                <p className="text-white font-semibold text-sm uppercase tracking-wider">Party Package Details</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls}>Event Date *</label>
                    <input
                      name="eventDate"
                      type="date"
                      value={form.eventDate}
                      onChange={e => set('eventDate', e.target.value)}
                      className={inputCls('eventDate')}
                    />
                    {errors.eventDate && <p className="text-red-200 text-xs mt-1">{errors.eventDate}</p>}
                  </div>
                  <div>
                    <label className={labelCls}>Number of Guests *</label>
                    <select
                      name="guestCount"
                      value={form.guestCount}
                      onChange={e => set('guestCount', e.target.value)}
                      className={inputCls('guestCount')}
                    >
                      <option value="">Select range</option>
                      <option>1–10</option>
                      <option>11–20</option>
                      <option>21–30</option>
                      <option>31–50</option>
                      <option>50+</option>
                    </select>
                    {errors.guestCount && <p className="text-red-200 text-xs mt-1">{errors.guestCount}</p>}
                  </div>
                  <div>
                    <label className={labelCls}>Child's Age</label>
                    <input
                      name="childAge"
                      type="text"
                      value={form.childAge}
                      onChange={e => set('childAge', e.target.value)}
                      placeholder="e.g. 5 years old"
                      className={inputCls('childAge')}
                    />
                  </div>
                  <div>
                    <label className={labelCls}>Party Theme</label>
                    <input
                      name="partyTheme"
                      type="text"
                      value={form.partyTheme}
                      onChange={e => set('partyTheme', e.target.value)}
                      placeholder="e.g. Unicorn, Superhero, Princess"
                      className={inputCls('partyTheme')}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className={labelCls}>Approximate Budget</label>
                    <select
                      name="partyBudget"
                      value={form.partyBudget}
                      onChange={e => set('partyBudget', e.target.value)}
                      className={inputCls('partyBudget')}
                    >
                      <option value="">Select budget range</option>
                      <option>Under $100</option>
                      <option>$100 – $250</option>
                      <option>$250 – $500</option>
                      <option>$500 – $1,000</option>
                      <option>$1,000+</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* Women's Collection Fields */}
            {form.requestType === 'womens' && (
              <div className="space-y-4 border-t border-white/20 pt-5">
                <p className="text-white font-semibold text-sm uppercase tracking-wider">Collection Preferences</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls}>Collection Interest</label>
                    <select
                      name="collectionInterest"
                      value={form.collectionInterest}
                      onChange={e => set('collectionInterest', e.target.value)}
                      className={inputCls('collectionInterest')}
                    >
                      <option value="">Select category</option>
                      <option>Dresses</option>
                      <option>Tops & Blouses</option>
                      <option>Trousers & Skirts</option>
                      <option>Outerwear</option>
                      <option>Accessories</option>
                      <option>Full Outfit Styling</option>
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Clothing Size</label>
                    <select
                      name="sizes"
                      value={form.sizes}
                      onChange={e => set('sizes', e.target.value)}
                      className={inputCls('sizes')}
                    >
                      <option value="">Select size</option>
                      <option>XS</option>
                      <option>S</option>
                      <option>M</option>
                      <option>L</option>
                      <option>XL</option>
                      <option>XXL</option>
                      <option>Plus Size</option>
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className={labelCls}>Style Preference</label>
                    <select
                      name="stylePreference"
                      value={form.stylePreference}
                      onChange={e => set('stylePreference', e.target.value)}
                      className={inputCls('stylePreference')}
                    >
                      <option value="">Select style</option>
                      <option>Casual & Everyday</option>
                      <option>Smart Casual</option>
                      <option>Formal & Office</option>
                      <option>Evening & Occasion</option>
                      <option>Bohemian & Relaxed</option>
                      <option>Trendy & Fashion-forward</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* Message */}
            <div>
              <label className={labelCls}>Additional Details</label>
              <textarea
                name="message"
                value={form.message}
                onChange={e => set('message', e.target.value)}
                placeholder="Tell us anything else that would help us serve you better..."
                rows={4}
                maxLength={500}
                className={`${inputCls('message')} resize-none`}
              />
              <div className="flex justify-between mt-1">
                {errors.message
                  ? <p className="text-red-200 text-xs">{errors.message}</p>
                  : <span />}
                <p className="text-white/60 text-xs">{form.message.length}/500</p>
              </div>
            </div>

            {/* Submit */}
            <div className="pt-2">
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-4 bg-[#574407] text-white rounded-xl font-bold text-base hover:bg-[#3a2a00] transition-colors duration-300 disabled:opacity-50 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    Sending…
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-line"></i>
                    Send My Request
                  </>
                )}
              </button>
              {status === 'error' && (
                <p className="text-red-200 text-sm text-center mt-3">Something went wrong. Please try again.</p>
              )}
            </div>
          </form>
        )}
      </div>
    </section>
  );
}
